import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { Box, FormHelperText, InputLabel, Typography } from '@mui/material';
import BaseInputErrors from './BaseInputErrors';
import InputTooltip from './InputTooltip';
import { CommonInputLayoutProps } from 'framework/form';
import floatingInputSx from './floatingInputSx';

interface FLBaseInputLayoutProps extends Omit<CommonInputLayoutProps, 'children'> {
  type?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  children?: React.ReactNode;
  placeholderOnly?: boolean;
  shouldBeShrinkedByDefault?: boolean;
}

const FLBaseInputLayout = memo(
  ({
    source,
    label,
    errorMessages = [],
    isVisible = true,
    isRequired = false,
    helpText = null,
    tooltip = null,
    className = '',
    placeholder,
    children,
    labelHint,
    errorMessageMapper,
    placeholderOnly,
    shouldBeShrinkedByDefault,
    sx,
    ...props
  }: FLBaseInputLayoutProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    const getBorderColor = (hasError: boolean, focused: boolean) => {
      if (hasError) return 'error.main';
      if (focused) return 'primary.main';
      return 'common.commonInputBorder';
    };

    const getLabelColor = (hasError: boolean, focused: boolean) => {
      if (hasError) return 'error.main';
      if (focused) return 'primary.main';
      return 'text.secondary';
    };

    useEffect(() => {
      const handleFocusIn = (e: FocusEvent) => {
        const container = containerRef.current;
        if (container && container.contains(e.target as Node)) {
          setIsFocused(true);
        }
      };

      const handleFocusOut = (e: FocusEvent) => {
        const container = containerRef.current;
        if (container && !container.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
        }
      };

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      // eslint-disable-next-line consistent-return
      return () => {
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const hasError = !!errorMessages?.length;

    const labelText = typeof label === 'function' ? label() : label || placeholder;

    const shouldBeShrinked = !!props.value || isFocused || shouldBeShrinkedByDefault;

    const requiredValue = typeof isRequired === 'function' ? isRequired({}) : !!isRequired;

    const renderLabel = useCallback(() => {
      if (!tooltip) {
        return (
          <>
            {labelText}
            {requiredValue && <span> *</span>}
          </>
        );
      }

      return (
        <>
          {labelText}
          {requiredValue && <span> *</span>}
          <InputTooltip
            message={tooltip}
            source={source}
            size="small"
            sx={{
              pointerEvents: 'all',
            }}
          />
        </>
      );
    }, [tooltip, labelText, source, requiredValue]);

    if (!isVisible) return null;

    return (
      <Box
        ref={containerRef}
        className={`floating-label-baseInput ${className}`}
        sx={[
          floatingInputSx,
          {
            transition: 'padding 0.3s ease',
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {/* MUI-style fieldset with notched outline */}
        <Box position="relative">
          <Box
            component="fieldset"
            aria-hidden="true"
            className="floating-base-fieldset"
            sx={{
              textAlign: 'left',
              position: 'absolute',
              bottom: 0,
              right: 0,
              top: '-5px',
              left: 0,
              margin: 0,
              padding: '0 8px',
              pointerEvents: 'none',
              borderRadius: '4px',
              borderStyle: 'solid',
              borderWidth: '1px',
              overflow: 'hidden',
              minWidth: '0%',
              zIndex: 1,
              borderColor: getBorderColor(hasError, isFocused),
            }}
          >
            <Box
              component="legend"
              className={`floating-base-legend ${shouldBeShrinked && labelText ? 'shrunk' : 'normal'}`}
              sx={{
                float: 'unset',
                width: 'auto',
                overflow: 'hidden',
                display: 'block',
                height: '11px',
                fontSize: '0.75em',
                whiteSpace: 'nowrap',
                padding: 0,
                transition: shouldBeShrinked
                  ? 'max-width 100ms cubic-bezier(0, 0, 0.2, 1) 50ms'
                  : 'max-width 50ms cubic-bezier(0, 0, 0.2, 1) 0ms',
                visibility: shouldBeShrinked ? 'visible' : 'hidden',
                maxWidth: shouldBeShrinked && labelText ? '100%' : '0',
              }}
            >
              <Typography
                component="span"
                variant="inherit"
                sx={{
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  display: 'inline-block',
                  opacity: 0,
                  visibility: 'hidden',
                }}
              >
                {renderLabel()}
              </Typography>
            </Box>
          </Box>

          {labelText && !placeholderOnly && (
            <InputLabel
              htmlFor={source}
              shrink={shouldBeShrinked}
              error={hasError}
              className={`floating-base-input-label ${shouldBeShrinked ? 'shrunk' : 'normal'}`}
              sx={{
                '&&': {
                  color: getLabelColor(hasError, isFocused),
                  maxWidth: 'calc(100% - 45px)',
                  textOverflow: 'ellipsis',
                  position: 'absolute',
                  transformOrigin: 'top left',
                  padding: shouldBeShrinked ? '0 0.5' : 0,
                  backgroundColor: shouldBeShrinked ? 'background.paper' : 'transparent',
                  zIndex: 1,
                  pointerEvents: 'none',
                  transition:
                    'color 200ms cubic-bezier(0, 0, 0.2, 1), transform 200ms cubic-bezier(0, 0, 0.2, 1), max-width 200ms cubic-bezier(0, 0, 0.2, 1)',
                  top: shouldBeShrinked ? '-5px' : 0,
                  left: '2px',
                  transform: shouldBeShrinked ? 'translate(12px, -2px) scale(0.75)' : 'translate(16px, 9px)',
                },
              }}
            >
              {renderLabel()}
            </InputLabel>
          )}

          {children}
        </Box>

        {labelHint && (
          <FormHelperText
            sx={{
              position: 'absolute',
              top: '-8px',
              right: '12px',
              fontSize: '0.65rem',
              color: 'text.secondary',
              backgroundColor: 'background.paper',
              padding: '0 4px',
              zIndex: 1,
              transition: 'all 0.2s ease-out',
              margin: 0,
            }}
          >
            {labelHint}
          </FormHelperText>
        )}

        {(hasError || helpText) && (
          <FormHelperText
            component="div"
            error={hasError}
            sx={{ position: 'relative', left: 0, zIndex: 1, fontSize: '0.7rem', lineHeight: 1.2 }}
          >
            {hasError ? (
              <BaseInputErrors errorMessages={errorMessages} errorMessageMapper={errorMessageMapper} />
            ) : (
              helpText
            )}
          </FormHelperText>
        )}
      </Box>
    );
  },
);

export default FLBaseInputLayout;
